import type { AlgoliaProductType } from '@integration-layer/modules/05.productServices/runtime/components/WrapperProductTile.props'
import { SIDEBAR_IDS } from '@design-system/data/sidebarIds'

/**
 * Represents a brand option for filtering search results.
 * @typedef {Object} BrandOption
 * @property {string} label - The display name of the brand.
 * @property {string} value - The unique identifier for the brand.
 * @property {boolean} disabled - Indicates whether the brand option is currently disabled.
 */
type BrandOption = {
  label: string
  value: string
  disabled: boolean
}

/**
 * Represents a search result item containing product information.
 * @typedef {Object} SearchResultItem
 * @property {string} id - Unique identifier for the search result.
 * @property {string} name - Name of the brand or category.
 * @property {AlgoliaProductType[]} products - List of products in the search result.
 * @property {number} page - Current page number of the search results.
 * @property {number} total - Total number of products in the search result.
 * @property {boolean} isVisible - Indicates whether the result is currently visible.
 * @property {boolean} showMore - Flag to show or hide additional products.
 * @property {boolean} [isLoading] - Optional flag indicating loading state.
 */
type SearchResultItem = {
  id: string
  name: string
  products: AlgoliaProductType[]
  page: number
  total: number
  isVisible: boolean
  showMore: boolean
  isLoading?: boolean
}

/**
 * Composable for managing search functionality across multiple brands.
 *
 * @returns {Object} Search-related methods and reactive state
 * @property {Ref<string>} searchValue - The current search query.
 * @property {Ref<string[]>} selectedBrands - Currently selected brands.
 * @property {Ref<BrandOption[]>} brandOptions - Available brand filter options.
 * @property {Ref<SearchResultItem[]>} results - Search results for different brands.
 * @property {Ref<boolean>} isLoadingResults - Indicates if search results are being loaded.
 * @property {Function} openDialog - Opens the global search modal.
 * @property {Function} closeDialog - Closes the global search modal.
 * @property {Function} setBrandResultPage - Loads additional pages for a specific brand.
 * @property {Function} resetQuery - Clears the current search query.
 * @property {Function} smartLoadMore - Loads more products for a specific brand.
 */
export const useSearch = () => {
  const { saveRecentSearch } = useRecentSearches()
  const { search } = useXgen()
  const { skuPrices, fetchSkuPrices } = useProductTileClPrices()
  const {
    removeAllFilters,
    activeFiltersCounter,
    closeFiltersBox,
    mergeFacets,
    filters,
  } = useSearchFilters()
  const { ts } = useI18n()
  const { currentWebsite } = useWebsite()

  const modalId = SIDEBAR_IDS.globalSearchModal

  const { isDialogOpen, openDialog, closeDialog } = useDialog(modalId)

  // TODO: (tracked #1263) searchable brands from Ampliance
  const brands = [
    {
      label: 'Giorgio Armani',
      value: 'giorgio-armani',
    },
    {
      label: 'Emporio Armani',
      value: 'emporio-armani',
    },
    {
      label: 'Ea7',
      value: 'ea7',
    },
    {
      label: 'Armani Exchange',
      value: 'armani-exchange',
    },
    {
      label: 'Armani Casa',
      value: 'armani-casa',
    },
  ]

  const brandOptions = ref<BrandOption[]>([
    { label: ts('search.brandSelector.all'), value: 'all', disabled: false },
    ...brands.map(el => ({ ...el, disabled: false })),
  ])
  const selectedBrands = ref<Array<string>>(['all'])

  const lastClickedResult = ref<string>('')
  const route = useRoute()
  const searchValue = ref('')
  const isLoadingResults = ref<boolean>(false)
  const results = ref<Array<SearchResultItem>>([])
  const selectedFilters = useState<{ [key: string]: string[] }>(
    'selected-filters'
  )
  const previousSelectedBrands = ref<Array<string>>([])

  watch(isDialogOpen, () => {
    const externalBrand = brands.find(
      brand => brand.value === currentWebsite.value
    )
    if (previousSelectedBrands.value.length) {
      selectedBrands.value = previousSelectedBrands.value
    } else {
      selectedBrands.value = [
        currentWebsite.value === 'global' || !externalBrand
          ? 'all'
          : currentWebsite.value,
      ]
    }
  })

  watch(route, () => {
    previousSelectedBrands.value = []
  })

  /**
   * Fetches search recommendations for specified brands with optional filters.
   *
   * @param {BrandOption[]} searchableBrands - List of brands to search.
   * @param {Object} [appliedFilters] - Optional filters to apply to the search.
   * @returns {Promise<Array>} Search results for each brand.
   */
  const fetchRecommendations = async (
    searchableBrands: BrandOption[],
    appliedFilters?: { [key: string]: string[] }
  ) => {
    return await Promise.all(
      searchableBrands.map(async brand => {
        // 2.0 Avoid fetching results if the search value is empty
        const response = searchValue.value
          ? await search({
              q: searchValue.value,
              brand: brand.label,
              color: appliedFilters?.Color,
              microCat: appliedFilters?.microCat,
              macroCat: appliedFilters?.macroCat,
              gender: appliedFilters?.Gender,
              age: appliedFilters?.Age,
              size: appliedFilters?.size,
            })
          : {
              products: [],
              facets: {},
              page: 0,
              total: 0,
              urlRedirect: undefined,
            }

        //2.1 Fetch prices from CL
        if (response.products && response.products.length) {
          const skuList = response.products
            .map((product: AlgoliaProductType) => product.size[0].SKU)
            .slice(0, 4)

          await fetchSkuPrices(skuList)

          const productsWithClPrice = response.products.map(
            (product: AlgoliaProductType) => {
              if (
                product.size[0].SKU &&
                skuPrices.value?.[product.size[0].SKU]
              ) {
                return {
                  ...product,
                  clPrices: skuPrices.value?.[product.size[0].SKU],
                }
              }
              return product
            }
          )

          return {
            ...response,
            products: productsWithClPrice,
          }
        }

        return response
      })
    )
  }

  /**
   * @watcher SelectedBrands
   * @description Add or remove the `all` value based on the brands selected by the user.
   * This ensures that when a specific brand is selected, the `all` option is deselected and vice versa.
   */
  watch(
    () => selectedBrands.value,
    async (_newValue, _oldValue) => {
      if (
        (_newValue.includes('all') && !_oldValue.includes('all')) ||
        _newValue.length === 0
      )
        nextTick(() => {
          selectedBrands.value = ['all']
        })
      else if (_oldValue.includes('all') && _newValue.length > 1) {
        nextTick(() => {
          selectedBrands.value = _newValue.filter(el => el !== 'all')
        })
      }

      previousSelectedBrands.value = _newValue

      isLoadingResults.value = true

      /**
       * If 'all' is selected, return and wait for the `SelectedBrands` watcher to clean up the selected brands.
       */
      if (
        selectedBrands.value.includes('all') &&
        selectedBrands.value.length > 1
      ) {
        return
      }

      /**
       * Update the results
       */
      try {
        // 1. Remove the `all` value from the searchableBrands array to exclude it from the search
        const searchableBrands = brandOptions.value.filter(el =>
          selectedBrands.value.includes('all')
            ? el.value !== 'all'
            : selectedBrands.value.includes(el.value)
        )

        // 2. Perform a search for each brand, including those that are not selected, and wait for all results
        const searchableBrandsResults = await fetchRecommendations(
          searchableBrands,
          selectedFilters.value
        )

        const oldResults = results.value.slice()
        results.value = []

        filters.value = mergeFacets(searchableBrandsResults)

        searchableBrands.forEach(async (brand, index) => {
          // 3. Update the validity of each brand based on the search results
          const currentBrandOption = brandOptions.value.find(
            el => el.value === brand.value && el.value !== 'all'
          )
          if (currentBrandOption) {
            // Disable the brand option if there is a searchValue and there are no search results.
            if (searchValue.value && !searchableBrandsResults[index]?.total) {
              currentBrandOption.disabled = true
            } else {
              currentBrandOption.disabled = false
            }
          }

          // 4. Add the results to the results array
          if (
            searchableBrandsResults[index].total &&
            searchableBrandsResults[index].products.length > 0
          ) {
            const oldResultsForBrand = oldResults.find(
              i => i.id === brand.value
            )

            results.value.push({
              id: brand.value,
              name: brand.label,
              products: searchableBrandsResults[index].products,
              page: searchableBrandsResults[index].page,
              total: searchableBrandsResults[index].total,
              isVisible:
                selectedBrands.value.includes('all') ||
                selectedBrands.value.includes(brand.value),
              showMore: oldResultsForBrand?.showMore ?? false,
            })
          }
        })
      } finally {
        isLoadingResults.value = false
      }
    }
  )

  /**
   * @watcher SearchValue && SelectedBrands
   * @description When either `searchValue` or `selectedBrands` changes,
   * this logic ensures that all xGen components update their results
   * and deactivate any invalid brands.
   */
  watch(
    [() => searchValue.value],
    async ([searchValue], [oldSearchValue]) => {
      isLoadingResults.value = true

      /**
       * If 'all' is selected, return and wait for the `SelectedBrands` watcher to clean up the selected brands.
       */
      if (
        selectedBrands.value.includes('all') &&
        selectedBrands.value.length > 1
      ) {
        return
      }

      /**
       * Reset if the searched value is nullish
       */
      if (!searchValue) {
        results.value = []
        isLoadingResults.value = false
        brandOptions.value = brandOptions.value.map(brand => ({
          ...brand,
          disabled: false,
        }))
        return
      }

      /**
       * Reset all filters if the searched value changes
       */
      if (searchValue !== oldSearchValue) {
        removeAllFilters()
        closeFiltersBox()
      }

      /**
       * Update the results
       */
      try {
        // 1. Remove the `all` value from the searchableBrands array to exclude it from the search
        const searchableBrands = brandOptions.value.filter(el =>
          selectedBrands.value.includes('all')
            ? el.value !== 'all'
            : selectedBrands.value.includes(el.value)
        )

        // 2. Perform a search for each brand, including those that are not selected, and wait for all results
        const searchableBrandsResults =
          await fetchRecommendations(searchableBrands)

        saveRecentSearch(searchValue)

        results.value = []

        filters.value = mergeFacets(searchableBrandsResults)

        searchableBrands.forEach(async (brand, index) => {
          // 3. Update the validity of each brand based on the search results
          const currentBrandOption = brandOptions.value.find(
            el => el.value === brand.value && el.value !== 'all'
          )
          if (currentBrandOption) {
            if (searchableBrandsResults[index].total === 0) {
              currentBrandOption.disabled = true
            } else {
              currentBrandOption.disabled = false
            }
          }

          // 4. Add the results to the results array
          if (
            searchableBrandsResults[index].total &&
            searchableBrandsResults[index].products.length > 0
          ) {
            results.value.push({
              id: brand.value,
              name: brand.label,
              products: searchableBrandsResults[index].products,
              page: searchableBrandsResults[index].page,
              total: searchableBrandsResults[index].total,
              isVisible:
                selectedBrands.value.includes('all') ||
                selectedBrands.value.includes(brand.value),
              showMore: false,
            })
          }
        })
      } finally {
        isLoadingResults.value = false
      }
    },
    { deep: true }
  )

  watch(
    [() => ({ ...selectedFilters.value })],
    async ([appliedFilters]) => {
      isLoadingResults.value = true

      /**
       * Update the results
       */
      try {
        // 1. Remove the `all` value from the searchableBrands array to exclude it from the search
        const searchableBrands = brandOptions.value.filter(el =>
          selectedBrands.value.includes('all')
            ? el.value !== 'all'
            : selectedBrands.value.includes(el.value)
        )

        // 2. Perform a search for each brand, including those that are not selected, and wait for all results
        const searchableBrandsResults = await fetchRecommendations(
          searchableBrands,
          appliedFilters
        )

        results.value = []

        filters.value = mergeFacets(searchableBrandsResults)

        searchableBrands.forEach(async (brand, index) => {
          // 3. Update the validity of each brand based on the search results
          const currentBrandOption = brandOptions.value.find(
            el => el.value === brand.value && el.value !== 'all'
          )
          if (currentBrandOption) {
            if (searchableBrandsResults[index].total === 0) {
              currentBrandOption.disabled = true
            } else {
              currentBrandOption.disabled = false
            }
          }

          // 4. Add the results to the results array
          if (
            searchableBrandsResults[index].total &&
            searchableBrandsResults[index].products.length > 0
          ) {
            results.value.push({
              id: brand.value,
              name: brand.label,
              products: searchableBrandsResults[index].products,
              page: searchableBrandsResults[index].page,
              total: searchableBrandsResults[index].total,
              isVisible:
                selectedBrands.value.includes('all') ||
                selectedBrands.value.includes(brand.value),
              showMore: false,
            })
          }
        })
      } finally {
        isLoadingResults.value = false
      }
    },
    { deep: 1 }
  )

  /**
   * Sets the result page for a specific brand.
   *
   * @param {string} brand - The brand identifier.
   * @param {number} page - The page number to load.
   */
  const setBrandResultPage = async (brand: string, page: number) => {
    const currentResult = results.value.find(
      (item: SearchResultItem) => item.id === brand
    )

    if (currentResult) {
      currentResult.isLoading = true

      try {
        const response = await search({
          q: searchValue.value,
          brand: currentResult.name,
          page,
        })

        // Fetch prices from CL
        const skuList = response.products.map(
          (product: AlgoliaProductType) => product.size[0].SKU
        )

        await fetchSkuPrices(skuList)

        const productsWithClPrice = response.products.map(
          (product: AlgoliaProductType) => {
            if (product.size[0].SKU && skuPrices.value?.[product.size[0].SKU]) {
              return {
                ...product,
                clPrices: skuPrices.value?.[product.size[0].SKU],
              }
            }
            return product
          }
        )

        if (response.products?.length > 0) {
          currentResult.products.push(...productsWithClPrice)
          currentResult.page = response.page
        }

        currentResult.isLoading = false
      } catch (error) {
        currentResult.isLoading = false
        console.error(error)
      }
    }
  }

  /**
   * Resets the search query to an empty string.
   */
  const resetQuery = () => (searchValue.value = '')

  /**
   * Loads more products for a specific brand and updates their prices.
   *
   * @param {string} brand - The brand identifier.
   */
  const smartLoadMore = async (brand: string) => {
    const currentResult = results.value.find(item => item.id === brand)

    if (
      currentResult &&
      currentResult.products?.length &&
      currentResult.products.length > 4
    ) {
      currentResult.isLoading = true

      try {
        // Fetch prices from CL
        const skuList = currentResult.products
          .map(product => product.size[0].SKU)
          .filter(isNonNullable)

        await fetchSkuPrices(skuList)

        const productsWithClPrice = currentResult.products.map(product => {
          if (product.size[0].SKU && skuPrices.value?.[product.size[0].SKU]) {
            return {
              ...product,
              clPrices: skuPrices.value?.[product.size[0].SKU],
            }
          }
          return product
        })

        currentResult.products = productsWithClPrice
      } catch (error) {
        console.error(error)
      } finally {
        currentResult.isLoading = false
        isLoadingResults.value = false
        currentResult.showMore = !currentResult.showMore
      }
    }
  }

  return {
    modalId,
    searchValue,
    selectedBrands,
    brandOptions,
    results,
    filters,
    isLoadingResults,
    isDialogOpen,
    lastClickedResult,
    openDialog,
    closeDialog,
    setBrandResultPage,
    resetQuery,
    smartLoadMore,
  }
}
